import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamesApi, progressApi, subscriptionApi } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ChildrenPanel from '../components/ChildrenPanel';

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [progress, setProgress] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    loadData();
    
    // Check for subscription success
    if (searchParams.get('subscription') === 'success') {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [gamesData, progressData, subData] = await Promise.all([
        gamesApi.list(),
        progressApi.getAll(),
        subscriptionApi.getStatus()
      ]);
      
      setGames(gamesData.games || []);
      setProgress(progressData.progress || []);
      setSubscription(subData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { url } = await subscriptionApi.createPortal();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  // Find progress for each game
  const getGameProgress = (gameId) => {
    return progress.find(p => p.gameId === gameId);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center">
            <span className="text-2xl mr-3">🎉</span>
            <div>
              <strong>Welcome to {subscription?.plan}!</strong> Your subscription is now active. Enjoy unlimited learning!
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="mt-2 text-gray-600">
            Ready to play and learn today?
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Current Plan</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">
                  {subscription?.plan || 'Free'}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">⭐</span>
              </div>
            </div>
            {subscription?.plan === 'free' && (
              <Link to="/pricing" className="mt-4 text-sm text-primary-600 font-medium hover:text-primary-700 block">
                Upgrade for more →
              </Link>
            )}
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Levels Unlocked</p>
                <p className="text-2xl font-bold text-gray-900">
                  {subscription?.features?.maxLevels === -1 ? '∞' : subscription?.features?.maxLevels || 20}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🔓</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Play Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPlayTime(progress.reduce((sum, p) => sum + (p.totalPlayTime || 0), 0))}
                </p>
              </div>
              <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">⏱️</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sessions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {progress.reduce((sum, p) => sum + (p.sessionCount || 0), 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🎮</span>
              </div>
            </div>
          </div>
        </div>

        {/* Children - the record engine progress hangs off */}
        <ChildrenPanel />

        {/* Pip's Journey Demo - Featured */}
        <div className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
            🌟 Featured Demo
          </h2>
          <div className="card-hover bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white overflow-hidden rounded-2xl p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-[#2dd4bf] text-[#1e3a5f] text-xs font-bold rounded-full">NEW</span>
                  <span className="px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full">10 Phases</span>
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">
                  Pip's Journey Demo
                </h3>
                <p className="text-white/80 mb-4">
                  Experience all 10 phases of speech development in this interactive demo. 
                  See how voice-powered games transform speech therapy into play!
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-white/10 text-white/90 text-xs rounded-full">Voice Control</span>
                  <span className="px-2 py-1 bg-white/10 text-white/90 text-xs rounded-full">20 Levels</span>
                  <span className="px-2 py-1 bg-white/10 text-white/90 text-xs rounded-full">All Ages</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="text-6xl">🗺️</div>
                <a 
                  href="/pips-journey.html"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2dd4bf] hover:bg-[#5eead4] text-[#1e3a5f] font-bold rounded-full transition-all transform hover:scale-105"
                >
                  ▶ Play Demo
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Games Section */}
        <div className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
            Your Games
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map(game => {
              const gameProgress = getGameProgress(game.id);
              const isLocked = game.isLocked;
              
              return (
                <div 
                  key={game.id} 
                  className={`card-hover relative overflow-hidden ${isLocked ? 'opacity-75' : ''}`}
                >
                  {/* Game Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-display text-xl font-bold text-gray-900">
                        {game.name}
                      </h3>
                      <p className="text-sm text-gray-500">Ages {game.ageRange}</p>
                    </div>
                    <div className="text-4xl">
                      {game.id === 'pip' ? '🐱' : '🎮'}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {game.description}
                  </p>

                  {/* Progress Bar */}
                  {gameProgress && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium text-gray-900">
                          Level {gameProgress.highestLevel} / {game.totalLevels}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500"
                          style={{ width: `${gameProgress.percentComplete}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {game.skills?.slice(0, 3).map((skill, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Action Button */}
                  {isLocked ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Requires {game.requiredPlan} plan
                      </span>
                      <Link 
                        to="/pricing"
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Upgrade
                      </Link>
                    </div>
                  ) : (
                    <Link 
                      to={`/play/${game.id}`}
                      className="btn-primary w-full"
                    >
                      {gameProgress ? 'Continue Playing' : 'Start Playing'}
                    </Link>
                  )}

                  {/* Locked Overlay */}
                  {isLocked && (
                    <div className="absolute top-4 right-4">
                      <span className="text-2xl">🔒</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Coming Soon Card */}
            <div className="card border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-center py-12">
              <span className="text-5xl mb-4">🚀</span>
              <h3 className="font-display text-xl font-bold text-gray-400">
                More Games Coming Soon!
              </h3>
              <p className="text-gray-400 mt-2 text-sm">
                New games added monthly
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Management */}
        {subscription?.plan !== 'free' && (
          <div className="card bg-gradient-to-r from-primary-50 to-accent-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-bold text-gray-900">
                  Manage Your Subscription
                </h3>
                <p className="text-gray-600 text-sm mt-1">
                  {subscription?.cancelAtPeriodEnd 
                    ? `Your subscription will end on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                    : `Next billing date: ${new Date(subscription?.currentPeriodEnd).toLocaleDateString()}`
                  }
                </p>
              </div>
              <button
                onClick={handleManageSubscription}
                className="btn-secondary"
              >
                Manage Billing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function
function formatPlayTime(seconds) {
  if (!seconds || seconds < 60) return '0 min';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}
